import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCloudUploadAlt } from "react-icons/fa";
import axios from "axios";
import ArticleEditor from "./Blogs/BlogEditor";

export default function CreateBlogForm() {
  const navigate = useNavigate();
  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const [blog, setBlog] = useState({
    type: "blog",
    title: "",
    author: "",
    date: "",
    tag: "",
    timeToRead: "",
    link: "",
    summary: "",
    content: "",
    image: null,
    content_pdf: null,
  });

  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    const isFile = files && files[0];
    setBlog({ ...blog, [name]: isFile ? files[0] : value });
    if (name === "image" && isFile) {
      if (files[0].size > 1 * 1024 * 1024) {
        alert("Image must be less than 1 MB");
        e.target.value = "";
        return;
      }
      setSelectedImageFile(files[0]);
    } else if (name === "content_pdf" && isFile) {
      setSelectedPdfFile(files[0]);
    }
  };
  

    const removeImageFile = () => {
      setSelectedImageFile(null);
      setBlog({ ...blog, image: null });
    };

    const removePdfFile = () => {
      setSelectedPdfFile(null);
      setBlog({ ...blog, content_pdf: null });
    };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();

    
      // Create blog_data object without files
      const blogData = {
        type: blog.type,
        title: blog.title,
        author: blog.author,
        date: blog.date,
        tag: blog.tag,
        timeToRead: blog.timeToRead,
        link: blog.link && blog.link.toString().trim() !== "" ? blog.link : null,
        summary: blog.summary,
        content: blog.type === "blog" ? blog.content : null,
      };

      formData.append("blog_data", JSON.stringify(blogData));
      if (blog.image instanceof File) {
        formData.append("image", blog.image);
      }
      if (blog.content_pdf instanceof File) {
        formData.append("content_pdf", blog.content_pdf);
      }

      axios
        .post(`${API_BASE}upload-blog`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          alert(res.data.message);
          navigate("/");
        })
        .catch((err) => {
          alert("Error uploading blog: " + (err.response?.data?.detail || err.message));
          console.error("Error uploading blog:", err);
        });
  };

  const valuesToFill = [
    {
      name: "title",
      label: "Title",
      placeholder:
        "Gen AI: The Challenge of Scaling with Data Security & Privacy",
      type: "text",
    },
    {
      name: "author",
      label: "Author",
      placeholder: "John Doe",
      type: "text",
    },
    {
      name: "date",
      label: "Date",
      placeholder: "01/01/2025",
      type: "date",
    },
    {
      name: "tag",
      label: "Tag",
      placeholder: "Enter tag",
      type: "text",
    },
    {
      name: "timeToRead",
      label: "Time to Read",
      placeholder: "5 min",
      type: "text",
    },
    {
      name: "link",
      label: "Link",
      placeholder: "https://www.linkedin.com/company/think41/",
      type: "text",
    },
    {
      name: "summary",
      label: "Summary",
      placeholder:
        "Wondering how to turn your Gen AI proof of concept into a full-scale solution—without compromising on data privacy and security? You're not alone.",
      type: "textarea",
    },
  ];

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-xl font-bold mb-4">Create Blog</h2>

      <div>
        <label htmlFor="type" className="block font-medium mb-1">
          Type
        </label>
        <select
          name="type"
          value={blog.type}
          onChange={handleChange}
          className="block w-full mb-2 p-2 border rounded"
        >
          <option value="blog">Blog</option>
          <option value="whitepaper">Whitepaper</option>
        </select>
      </div>

      {valuesToFill.map(({ name, label, placeholder, type }) => (
        <div key={name}>
          <label htmlFor={name} className="block font-medium mb-1">
            {label}
          </label>

          {name === "summary" || name === "content" ? (
            <textarea
              name={name}
              placeholder={placeholder}
              value={blog[name] || ""}
              onChange={handleChange}

              rows={name === "summary" ? 4 : 12}
              className="block w-full mb-2 p-2 border rounded"
              required
            />
          ) : (
            <input
              type={type}
              name={name}
              placeholder={placeholder}
              value={blog[name] || ""}
              onChange={handleChange}
              className="block w-full mb-2 p-2 border rounded"
              required={name !== "link"}
            />
          )}
        </div>
      ))}

      {blog.type === "blog" ? (
        <div className="mb-4">
          <label className="block font-medium mb-1">Content</label>
          <ArticleEditor
            content={blog.content}
            setContent={(html) => setBlog({ ...blog, content: html })}
          />
        </div>
      ) : (
        <>
          <label className="block font-medium mb-1">Content PDF</label>
          <div className="flex items-center gap-3 mb-4">
            <label
              htmlFor="pdfUpload"
              className="flex items-center gap-2 px-4 py-2 border rounded cursor-pointer text-blue-600 hover:bg-blue-50"
            >
              <FaCloudUploadAlt className="text-xl" />
              {selectedPdfFile ? "Change File" : "Upload PDF"}
            </label>
            <input
              id="pdfUpload"
              name="content_pdf"
              type="file"
              accept=".pdf"
              onChange={handleChange}
              className="hidden"
              required
            />
            {selectedPdfFile && (
              <>
                <span className="text-sm text-gray-700">{selectedPdfFile.name}</span>
                <button
                  onClick={removePdfFile}
                  type="button"
                  className="text-red-500 text-sm underline"
                >
                  Remove
                </button>
              </>
            )}
          </div>
        </>
      )}

      <label className="block font-medium mb-1">Image</label>
      <div className="flex items-center gap-3 mb-4">
        <label
          htmlFor="imageUpload"
          className="flex items-center gap-2 px-4 py-2 border rounded cursor-pointer text-blue-600 hover:bg-blue-50"
        >
          <FaCloudUploadAlt className="text-xl" />
          {selectedImageFile ? "Change File" : "Upload Image"}
        </label>
        <input
          id="imageUpload"
          name="image"
          type="file"
          onChange={handleChange}
          className="hidden"
        />
        {selectedImageFile && (
          <>
            <span className="text-sm text-gray-700">{selectedImageFile.name}</span>
            <button
              onClick={removeImageFile}
              type="button"
              className="text-red-500 text-sm underline"
            >
              Remove
            </button>
          </>
        )}
      </div>
      <button className="bg-green-600 text-white px-4 py-2 rounded cursor-pointer">
        Create
      </button>
    </form>
  );
}
